/**
 * PORT of `_reference/TC Visuals/Documentation/toast-module/src/toast.js` (verified
 * byte-identical to the live TC SPA copy). Adopted into the Pagy builder.
 *
 * Mechanical rename (order matters — specific before generic):
 *   toast-message → pb-toast-text; toast-action-btn → pb-toast-action;
 *   toast-hide → pb-toast-exit; toast-show → pb-toast-show; --toast- → --pb-toast-;
 *   (?<![\w-])toast- → pb-toast- (word boundary); ['toast' → ['pb-toast'.
 *   (`kind: 'toast'` internal sentinel and `auto-toast-<n>` record ids are left untouched.)
 * NOTE: the plan's `\btoast-` sed step does not run under macOS BSD sed and would double-
 *   prefix `pb-toast-*` under GNU sed; the canonical audit transform is the perl lookbehind
 *   above (see builder/docs/error-log.md).
 *
 * Tagged divergences (every intentional edit carries `// [builder-divergence N]`):
 *   1  renderToastIcon: no FA <i> child (empty span → CSS :empty::before glyphs); TYPE_ICON_CLASSES deleted.
 *   2  mountToast stamps `toast.dataset.toastId = record.id` (locked DOM contract).
 *   3  bare default type success → info (D2), two normalizeType fallback sites.
 *   4  MAX_VISIBLE_TOASTS 3 → 4 (D5).
 *   5  export showUndoToast → showTransactionalUndoToast (D3).
 *   6  showTransactionalUndoToast accepts options.id (same-key-commits-before-replace).
 *   7  new exports: dismissToastById (immediate slot-freeing teardown, for the error coalescer's
 *      cap-3 eviction) + dismissToastsByPrefix (also sweeps queued records); dismissRecord gained
 *      an optional `immediate` param (default false → unchanged for dismissToast/dismissAll/prefix).
 *
 * Canonical toast manager for transient, actionable, and long-running notices.
 *
 * Three toasts may be visible at once. Queued notices are priority ordered while
 * preserving FIFO within a priority. The non-transactional queue is capped at 12:
 * passive notices expire after 30 seconds, while excess protected notifications
 * collapse into an explicit summary. Transactional Undo records may exceed that
 * cap because dropping one would lose a pending callback. Urgent notices replace
 * a lower-priority passive notice when possible so critical feedback is visible.
 */
const MAX_VISIBLE_TOASTS = 4; // [builder-divergence 4] TC ships 3; the builder keeps an
// always-visible slot for a transactional undo even during a cap-3 preview-error storm (D5).
const MAX_QUEUED_TOASTS = 12;
const QUEUED_TOAST_TTL_MS = 30000;
const TOAST_EXIT_MS = 300;
const OVERFLOW_SUMMARY_ID = 'pb-toast-queue-overflow-summary';
const VALID_TYPES = new Set(['success', 'error', 'warning', 'info']);
// [builder-divergence 1] TYPE_ICON_CLASSES (Font Awesome) removed — the builder has no icon
// font; CSS `.pb-toast-icon:empty::before` renders text glyphs instead.

const toastsById = new Map();
const activeToasts = [];
const queuedToasts = [];
let resettingToasts = false;
let toastIdCounter = 0;
let queueSequence = 0;

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function normalizeType(value, fallback = 'success') {
  return VALID_TYPES.has(value) ? value : fallback;
}

function toastContainer() {
  let container = document.getElementById('pb-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'pb-toast-container';
    container.className = 'pb-toast-container';
    document.body.appendChild(container);
  }
  container.classList.add('pb-toast-container');
  container.dataset.pbKit = 'toast';
  container.setAttribute('aria-label', 'Notifications');
  return container;
}

function clearRecordTimers(record) {
  if (record.hideTimer) clearTimeout(record.hideTimer);
  if (record.queueExpiryTimer) clearTimeout(record.queueExpiryTimer);
  if (record.undoCommitTimer) clearTimeout(record.undoCommitTimer);
  if (record.undoTickTimer) clearInterval(record.undoTickTimer);
  record.hideTimer = null;
  record.queueExpiryTimer = null;
  record.undoCommitTimer = null;
  record.undoTickTimer = null;
}

function forgetRecord(record) {
  if (toastsById.get(record.id) === record) toastsById.delete(record.id);
}

function rethrowCollectedErrors(errors, context) {
  if (!errors.length) return;
  if (errors.length === 1) throw errors[0];
  throw new AggregateError(errors, `${context} failed in ${errors.length} toast callbacks`);
}

function abandonToastRecord(record) {
  if (!record) return;
  record.removed = true;
  if (record.kind === 'undo') record.undoSettled = true;
  clearRecordTimers(record);
  if (record.handleVisibilityChange) {
    document.removeEventListener('visibilitychange', record.handleVisibilityChange);
  }
  record.el?.remove();
  forgetRecord(record);
}

function resetDetachedToastState() {
  // Tests and auth-shell changes may replace <body>. Never promote notices that
  // belonged to the old shell into the new one.
  const container = document.getElementById('pb-toast-container');
  const detached = activeToasts.some(
    (record) => !record.el?.isConnected || record.el.parentElement !== container
  );
  if (container && !detached) return;
  const staleRecords = [...activeToasts, ...queuedToasts];
  activeToasts.splice(0);
  queuedToasts.splice(0);
  toastsById.clear();
  const callbackErrors = [];
  const wasResetting = resettingToasts;
  resettingToasts = true;
  try {
    staleRecords.forEach((record) => {
      try {
        if (record.kind === 'undo') {
          settleUndoRecord(record, 'commit', { immediate: true, promote: false });
        } else {
          abandonToastRecord(record);
        }
      } catch (error) {
        callbackErrors.push(error);
      }
    });
  } finally {
    resettingToasts = wasResetting;
  }
  rethrowCollectedErrors(callbackErrors, 'Detached toast teardown');
}

function renderOverflowIndicator() {
  const container = document.getElementById('pb-toast-container');
  if (!container) return;
  let indicator = container.querySelector('.pb-toast-overflow-indicator');
  if (!queuedToasts.length) {
    indicator?.remove();
    return;
  }
  if (!indicator) {
    indicator = document.createElement('div');
    indicator.className = 'pb-toast-overflow-indicator';
    indicator.setAttribute('aria-hidden', 'true');
  }
  indicator.textContent = `+${queuedToasts.length} waiting`;
  container.appendChild(indicator);
}

function normalizeToastInput(message, typeOrOptions) {
  let options = {};
  let legacyMessage = message;
  if (message && typeof message === 'object' && !Array.isArray(message)) {
    options = { ...message };
    legacyMessage = options.message || '';
  } else if (typeof typeOrOptions === 'string') {
    options.type = typeOrOptions;
  } else if (typeOrOptions && typeof typeOrOptions === 'object') {
    options = { ...typeOrOptions };
  }

  const explicitDetail = options.detail != null ? options.detail : options.description;
  let title = String(options.title || '').trim();
  let detail = '';
  let normalizedMessage = String(legacyMessage == null ? '' : legacyMessage).trim();

  if (explicitDetail != null) {
    detail = String(explicitDetail).trim();
    if (!title) title = normalizedMessage;
  } else if (title) {
    detail = normalizedMessage;
  }
  if (detail === title) detail = '';
  if (title) normalizedMessage = '';

  return { options, title, detail, message: normalizedMessage };
}

function readAction(options, current = { label: '', handler: null }, replace = false) {
  const hasAction = hasOwn(options, 'action');
  const hasHandler = hasOwn(options, 'onClick');
  if (!hasAction && !hasHandler && !replace) return current;
  if (!hasAction && !hasHandler) return { label: '', handler: null };

  let label = hasAction ? options.action : current.label;
  let handler = hasHandler ? options.onClick : current.handler;
  if (label && typeof label === 'object' && !Array.isArray(label)) {
    const action = label;
    label = action.label;
    if (hasOwn(action, 'onClick')) handler = action.onClick;
  }
  return {
    label: label == null ? '' : String(label).trim(),
    handler: typeof handler === 'function' ? handler : null
  };
}

function defaultToastDuration(type, actionable, loading = false) {
  if (loading) return 0;
  if (actionable) return 6500;
  if (type === 'error') return 7000;
  if (type === 'warning') return 5000;
  if (type === 'info') return 4000;
  return 3000;
}

function priorityFor(record) {
  if (record.kind === 'undo') return 5;
  if (record.kind === 'overflow') return 4;
  if (record.loading) return 4;
  if (record.type === 'error') return 3;
  if (record.actionLabel) return 2;
  if (record.type === 'warning') return 1;
  return 0;
}

function isProtectedRecord(record) {
  return (
    record.kind === 'undo' ||
    record.kind === 'overflow' ||
    record.loading ||
    record.type === 'error' ||
    !!record.actionLabel
  );
}

function setToastSemantics(record) {
  const toast = record.el;
  if (record.loading) {
    toast.setAttribute('role', 'status');
    // The initial operation is announced, then incremental progress mutations
    // are quiet. Resolving restores the final tone's live-region behavior.
    toast.setAttribute('aria-live', record.mounted ? 'off' : 'polite');
    toast.setAttribute('aria-busy', 'true');
  } else {
    toast.setAttribute('role', record.type === 'error' ? 'alert' : 'status');
    toast.setAttribute('aria-live', record.type === 'error' ? 'assertive' : 'polite');
    toast.removeAttribute('aria-busy');
  }
  toast.setAttribute('aria-atomic', 'true');
}

function renderToastCopy(record) {
  record.copy.replaceChildren();
  if (record.title) {
    const title = document.createElement('span');
    title.className = 'pb-toast-title';
    title.textContent = record.title;
    record.copy.appendChild(title);
    if (record.detail) {
      const detail = document.createElement('span');
      detail.className = 'pb-toast-detail';
      detail.textContent = record.detail;
      record.copy.appendChild(detail);
    }
    return;
  }
  const message = document.createElement('span');
  message.className = 'pb-toast-text';
  message.textContent = record.message || 'Notification';
  record.copy.appendChild(message);
}

function renderToastIcon(record) {
  if (!record.icon) {
    record.icon = document.createElement('span');
    record.icon.setAttribute('aria-hidden', 'true');
    record.el.insertBefore(record.icon, record.copy);
  }
  const visualType = record.loading ? 'loading' : record.type;
  record.icon.className = `pb-toast-icon pb-toast-icon-${visualType}`;
  // [builder-divergence 1] no icon-font <i> child; the empty span lets CSS
  // `.pb-toast-icon:empty::before` supply the glyph per type.
  record.icon.replaceChildren();
}

function renderToastAction(record) {
  record.actionBtn?.remove();
  record.actionBtn = null;
  if (!record.actionLabel) return;
  const actionBtn = document.createElement('button');
  actionBtn.type = 'button';
  actionBtn.className = 'pb-toast-action';
  actionBtn.textContent = record.actionLabel;
  actionBtn.addEventListener('click', function (event) {
    event.stopPropagation();
    if (record.removed || record.actionConsumed) return;
    record.actionConsumed = true;
    try {
      record.onActionClick?.();
    } finally {
      dismissRecord(record);
    }
  });
  record.actionBtn = actionBtn;
  record.el.appendChild(actionBtn);
}

function renderToastClose(record) {
  record.closeBtn?.remove();
  record.closeBtn = null;
  if (!record.dismissible) return;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'pb-toast-close-btn';
  closeBtn.setAttribute('aria-label', 'Dismiss notification');
  closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
  closeBtn.addEventListener('click', function (event) {
    event.stopPropagation();
    dismissRecord(record);
  });
  record.closeBtn = closeBtn;
  record.el.appendChild(closeBtn);
}

function setProgressPlayState(record, state) {
  if (record.progressEl) record.progressEl.style.animationPlayState = state;
}

function renderToastProgress(record) {
  record.progressEl?.remove();
  record.progressEl = null;
  if (record.loading) {
    const loading = document.createElement('span');
    loading.className = 'pb-toast-loading-progress';
    loading.setAttribute('aria-hidden', 'true');
    record.progressEl = loading;
    record.el.appendChild(loading);
  } else if (record.duration > 0) {
    const timed = document.createElement('span');
    timed.className = 'pb-toast-timer-progress';
    timed.setAttribute('aria-hidden', 'true');
    timed.style.setProperty('--pb-toast-duration', `${record.duration}ms`);
    timed.style.animationDuration = `${record.duration}ms`;
    record.progressEl = timed;
    record.el.appendChild(timed);
  }
  setProgressPlayState(record, record.pauseReasons.size ? 'paused' : 'running');
}

function renderUndoRecord(record) {
  const classes = ['pb-toast', `pb-toast-${record.type}`, 'pb-toast-undo'];
  if (record.el.isConnected) classes.push('pb-toast-show');
  record.el.className = classes.join(' ');
  record.el.dataset.type = record.type;
  record.el.setAttribute('role', 'status');
  record.el.setAttribute('aria-live', 'polite');
  record.el.setAttribute('aria-atomic', 'true');

  if (!record.undoMessageEl) {
    record.undoMessageEl = document.createElement('span');
    record.undoMessageEl.className = 'pb-toast-undo-message';
    record.el.appendChild(record.undoMessageEl);
  }
  record.undoMessageEl.textContent = record.message || 'Action scheduled';

  if (!record.undoCountdownEl) {
    record.undoCountdownEl = document.createElement('span');
    record.undoCountdownEl.className = 'pb-toast-undo-countdown';
    record.undoCountdownEl.setAttribute('aria-live', 'off');
    record.undoCountdownEl.setAttribute('aria-hidden', 'true');
    record.el.appendChild(record.undoCountdownEl);
  }
  renderUndoCountdown(record);

  if (!record.actionBtn) {
    const undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.className = 'pb-toast-action pb-toast-undo-btn';
    undoBtn.textContent = 'Undo';
    undoBtn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      settleUndoRecord(record, 'undo');
    });
    record.actionBtn = undoBtn;
    record.el.appendChild(undoBtn);
  }
}

function renderMountedRecord(record) {
  if (record.kind === 'undo') {
    renderUndoRecord(record);
    return;
  }
  const classes = ['pb-toast', `pb-toast-${record.type}`];
  if (record.loading) classes.push('pb-toast-loading');
  if (record.loading || record.legacyProgress) classes.push('pb-toast-progress');
  if (record.el.isConnected) classes.push('pb-toast-show');
  record.el.className = classes.join(' ');
  record.el.dataset.type = record.loading ? 'loading' : record.type;
  setToastSemantics(record);
  renderToastIcon(record);
  renderToastCopy(record);
  renderToastAction(record);
  renderToastClose(record);
  renderToastProgress(record);
}

function restartDismissTimer(record) {
  if (record.hideTimer) clearTimeout(record.hideTimer);
  record.hideTimer = null;
  record.remainingMs = record.duration;
  record.timerStartedAt = Date.now();
  renderToastProgress(record);
  if (record.duration <= 0 || record.pauseReasons.size) return;
  record.hideTimer = setTimeout(() => removeMountedToast(record), record.remainingMs);
}

function renderUndoCountdown(record) {
  if (!record.undoCountdownEl) return;
  record.undoCountdownEl.textContent = `${Math.max(0, Math.ceil(record.undoRemainingMs / 1000))}s`;
}

function clearUndoCountdownTimers(record) {
  if (record.undoCommitTimer) clearTimeout(record.undoCommitTimer);
  if (record.undoTickTimer) clearInterval(record.undoTickTimer);
  record.undoCommitTimer = null;
  record.undoTickTimer = null;
}

function armUndoCountdown(record) {
  if (
    record.kind !== 'undo' ||
    record.undoSettled ||
    record.removed ||
    !record.mounted ||
    record.pauseReasons.size
  ) {
    return;
  }
  clearUndoCountdownTimers(record);
  renderUndoCountdown(record);
  if (record.undoRemainingMs <= 0) {
    queueMicrotask(() => settleUndoRecord(record, 'commit'));
    return;
  }
  record.undoTimerStartedAt = Date.now();
  record.undoCommitTimer = setTimeout(
    () => settleUndoRecord(record, 'commit'),
    record.undoRemainingMs
  );
  record.undoTickTimer = setInterval(function () {
    if (record.undoSettled || record.removed || record.pauseReasons.size) return;
    const elapsed = Date.now() - record.undoTimerStartedAt;
    const visibleRemaining = Math.max(0, record.undoRemainingMs - elapsed);
    if (record.undoCountdownEl) {
      record.undoCountdownEl.textContent = `${Math.ceil(visibleRemaining / 1000)}s`;
    }
  }, 1000);
}

function pauseUndoCountdown(record) {
  if (record.kind !== 'undo' || record.undoSettled || !record.undoCommitTimer) return;
  record.undoRemainingMs = Math.max(
    0,
    record.undoRemainingMs - (Date.now() - record.undoTimerStartedAt)
  );
  clearUndoCountdownTimers(record);
  renderUndoCountdown(record);
}

function pauseTimerFor(record, reason) {
  if (record.removed || record.pauseReasons.has(reason)) return;
  const wasRunning = record.pauseReasons.size === 0;
  record.pauseReasons.add(reason);
  if (wasRunning && record.hideTimer) {
    clearTimeout(record.hideTimer);
    record.hideTimer = null;
    record.remainingMs = Math.max(0, record.remainingMs - (Date.now() - record.timerStartedAt));
  }
  if (wasRunning) pauseUndoCountdown(record);
  setProgressPlayState(record, 'paused');
}

function resumeTimerFor(record, reason) {
  if (record.removed || !record.pauseReasons.has(reason)) return;
  record.pauseReasons.delete(reason);
  if (record.pauseReasons.size) return;
  setProgressPlayState(record, 'running');
  armUndoCountdown(record);
  if (record.duration <= 0 || record.remainingMs <= 0 || record.hideTimer) return;
  record.timerStartedAt = Date.now();
  record.hideTimer = setTimeout(() => removeMountedToast(record), record.remainingMs);
}

function removeQueuedRecord(record) {
  if (record.removed) return;
  const queuedIndex = queuedToasts.indexOf(record);
  if (queuedIndex !== -1) queuedToasts.splice(queuedIndex, 1);
  record.removed = true;
  clearRecordTimers(record);
  forgetRecord(record);
  renderOverflowIndicator();
}

function removeMountedToast(record, { immediate = false, promote = true } = {}) {
  if (record.removed) return;
  record.removed = true;
  clearRecordTimers(record);
  forgetRecord(record);
  if (record.handleVisibilityChange) {
    document.removeEventListener('visibilitychange', record.handleVisibilityChange);
  }
  record.el.classList.remove('pb-toast-show');
  record.el.classList.add('pb-toast-exit');

  const finish = () => {
    record.el.remove();
    const activeIndex = activeToasts.indexOf(record);
    if (activeIndex !== -1) activeToasts.splice(activeIndex, 1);
    if (promote && !resettingToasts) drainToastQueue();
  };
  if (immediate) finish();
  else setTimeout(finish, TOAST_EXIT_MS);
}

function dismissRecord(record, { immediate = false } = {}) {
  // [builder-divergence 7] optional immediate teardown
  if (!record || record.removed) return;
  if (record.kind === 'undo') {
    settleUndoRecord(record, 'commit', { immediate });
    return;
  }
  if (record.mounted) removeMountedToast(record, { immediate });
  else removeQueuedRecord(record);
}

function settleUndoRecord(record, kind, { immediate = false, promote = true } = {}) {
  if (!record || record.kind !== 'undo' || record.undoSettled) return;
  record.undoSettled = true;
  clearUndoCountdownTimers(record);
  if (record.mounted) removeMountedToast(record, { immediate, promote });
  else removeQueuedRecord(record);

  const callback = kind === 'commit' ? record.onCommit : record.onUndo;
  if (callback) callback();
}

function sortToastQueue() {
  queuedToasts.sort(
    (left, right) => right.priority - left.priority || left.sequence - right.sequence
  );
}

function armQueueExpiry(record) {
  if (record.queueExpiryTimer) clearTimeout(record.queueExpiryTimer);
  record.queueExpiryTimer = null;
  // Never expire feedback that carries an unseen error, action, active operation,
  // or deferred transaction. A loading record receives a normal TTL only after
  // it resolves to a passive final state.
  if (isProtectedRecord(record)) return;
  record.queueExpiryTimer = setTimeout(() => removeQueuedRecord(record), QUEUED_TOAST_TTL_MS);
}

function makeRoomForUrgent(record) {
  if (!isProtectedRecord(record) || activeToasts.length < MAX_VISIBLE_TOASTS) return false;
  const passive = activeToasts.reduce((candidate, active) => {
    if (active.removed || isProtectedRecord(active) || active.priority >= record.priority) {
      return candidate;
    }
    if (!candidate || active.priority < candidate.priority) return active;
    return candidate;
  }, null);
  if (!passive) return false;
  removeMountedToast(passive, { immediate: true, promote: false });
  mountToast(record);
  return true;
}

function nonUndoQueuedCount() {
  return queuedToasts.reduce((count, queued) => count + (queued.kind === 'undo' ? 0 : 1), 0);
}

function oldestQueuedCandidate({ passiveOnly = false } = {}) {
  return queuedToasts
    .filter(
      (queued) =>
        queued.kind !== 'undo' &&
        queued.kind !== 'overflow' &&
        (!passiveOnly || !isProtectedRecord(queued))
    )
    .reduce((candidate, queued) => {
      if (!candidate || queued.priority < candidate.priority) return queued;
      if (queued.priority === candidate.priority && queued.sequence < candidate.sequence)
        return queued;
      return candidate;
    }, null);
}

function recordCollapsedNotifications(count) {
  if (count <= 0) return;
  const existing = toastsById.get(OVERFLOW_SUMMARY_ID);
  if (existing && !existing.removed) {
    existing.collapsedCount = (existing.collapsedCount || 0) + count;
    applyRecordUpdate(
      existing,
      normalizeToastInput({
        title: 'Notifications condensed',
        detail: `${existing.collapsedCount} older notifications were condensed`,
        type: 'warning',
        duration: 6500
      })
    );
    return;
  }

  const summary = makeRecord(
    normalizeToastInput({
      title: 'Notifications condensed',
      detail: `${count} older notifications were condensed`,
      type: 'warning',
      duration: 6500
    }),
    OVERFLOW_SUMMARY_ID
  );
  summary.kind = 'overflow';
  summary.collapsedCount = count;
  summary.priority = priorityFor(summary);
  summary.sequence = ++queueSequence;
  toastsById.set(summary.id, summary);
  queuedToasts.push(summary);
  sortToastQueue();
  armQueueExpiry(summary);
}

function reserveQueueCapacity(record) {
  if (record.kind === 'undo') return true;

  if (!isProtectedRecord(record)) {
    if (nonUndoQueuedCount() < MAX_QUEUED_TOASTS) return true;
    const passive = oldestQueuedCandidate({ passiveOnly: true });
    if (!passive || record.priority < passive.priority) return false;
    removeQueuedRecord(passive);
    return true;
  }

  if (nonUndoQueuedCount() < MAX_QUEUED_TOASTS) return true;

  const summary = toastsById.get(OVERFLOW_SUMMARY_ID);
  const needsSummarySlot = !summary || summary.removed;
  const requiredSlots = 1 + (needsSummarySlot ? 1 : 0);
  let collapsed = 0;
  while (nonUndoQueuedCount() > MAX_QUEUED_TOASTS - requiredSlots) {
    const passive = oldestQueuedCandidate({ passiveOnly: true });
    const victim = passive || oldestQueuedCandidate();
    if (!victim) break;
    removeQueuedRecord(victim);
    collapsed += 1;
  }
  recordCollapsedNotifications(collapsed);
  return nonUndoQueuedCount() < MAX_QUEUED_TOASTS;
}

function enqueueToast(record) {
  if (makeRoomForUrgent(record)) return;

  record.sequence = ++queueSequence;
  record.priority = priorityFor(record);
  if (!reserveQueueCapacity(record)) {
    record.removed = true;
    forgetRecord(record);
    return;
  }
  queuedToasts.push(record);
  sortToastQueue();
  armQueueExpiry(record);
  renderOverflowIndicator();
}

function drainToastQueue() {
  while (activeToasts.length < MAX_VISIBLE_TOASTS && queuedToasts.length) {
    const record = queuedToasts.shift();
    if (!record || record.removed) continue;
    if (record.queueExpiryTimer) clearTimeout(record.queueExpiryTimer);
    record.queueExpiryTimer = null;
    mountToast(record);
  }
  renderOverflowIndicator();
}

function mountToast(record) {
  const container = toastContainer();
  const toast = document.createElement('div');
  toast.dataset.pbKit = 'toast';
  let copy = null;
  if (record.kind !== 'undo') {
    copy = document.createElement('span');
    copy.className = 'pb-toast-copy';
    toast.appendChild(copy);
  }
  record.el = toast;
  record.copy = copy;
  toast.dataset.toastId = record.id; // [builder-divergence 2] locked DOM contract: prefix dismissal + iframe-error test count
  renderMountedRecord(record);

  toast.addEventListener('mouseenter', () => pauseTimerFor(record, 'hover'));
  toast.addEventListener('mouseleave', () => resumeTimerFor(record, 'hover'));
  toast.addEventListener('focusin', () => pauseTimerFor(record, 'focus'));
  toast.addEventListener('focusout', function (event) {
    if (!toast.contains(event.relatedTarget)) resumeTimerFor(record, 'focus');
  });
  toast.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    dismissRecord(record);
  });
  record.handleVisibilityChange = function () {
    if (document.hidden) pauseTimerFor(record, 'hidden');
    else resumeTimerFor(record, 'hidden');
  };
  document.addEventListener('visibilitychange', record.handleVisibilityChange);

  activeToasts.push(record);
  container.appendChild(toast);
  record.mounted = true;
  renderOverflowIndicator();
  requestAnimationFrame(function () {
    if (!toast.isConnected || record.removed) return;
    toast.classList.add('pb-toast-show');
    if (record.loading) toast.setAttribute('aria-live', 'off');
  });
  restartDismissTimer(record);
  armUndoCountdown(record);
  if (document.hidden) pauseTimerFor(record, 'hidden');
}

function applyRecordUpdate(record, normalized, { replace = false } = {}) {
  if (record.removed) return record.controller;
  const options = normalized.options;
  const previousType = record.type;
  const previousLoading = record.loading;
  const previousAction = record.actionLabel;

  record.title = normalized.title;
  record.detail = normalized.detail;
  record.message = normalized.message;
  const nextLoading = replace
    ? options.loading === true
    : hasOwn(options, 'loading')
      ? options.loading === true
      : record.loading;
  if (replace || hasOwn(options, 'type')) {
    record.type = normalizeType(
      options.type,
      replace ? (nextLoading ? 'info' : 'info') : record.type // [builder-divergence 3] bare default success→info (D2)
    );
  }
  record.loading = nextLoading;
  if (replace || hasOwn(options, 'dismissible')) {
    record.dismissible = options.dismissible !== false;
  }
  if (replace) record.legacyProgress = options._legacyProgress === true;

  const action = readAction(
    options,
    { label: record.actionLabel, handler: record.onActionClick },
    replace
  );
  record.actionLabel = action.label;
  record.onActionClick = action.handler;

  const stateChanged =
    previousType !== record.type ||
    previousLoading !== record.loading ||
    previousAction !== record.actionLabel;
  if (typeof options.duration === 'number') {
    record.duration = Math.max(0, options.duration);
  } else if (replace || stateChanged) {
    record.duration = defaultToastDuration(record.type, !!record.actionLabel, record.loading);
  }
  record.priority = priorityFor(record);

  if (record.mounted) {
    renderMountedRecord(record);
    restartDismissTimer(record);
  } else {
    sortToastQueue();
    armQueueExpiry(record);
    if (isProtectedRecord(record) && makeRoomForUrgent(record)) {
      const queuedIndex = queuedToasts.indexOf(record);
      if (queuedIndex !== -1) queuedToasts.splice(queuedIndex, 1);
    }
    renderOverflowIndicator();
  }
  return record.controller;
}

function outcomeOptions(type, autoDismissOrOptions, maybeOptions) {
  let options = {};
  if (typeof autoDismissOrOptions === 'boolean') {
    options = maybeOptions && typeof maybeOptions === 'object' ? { ...maybeOptions } : {};
    if (!autoDismissOrOptions && !hasOwn(options, 'duration')) options.duration = 0;
  } else if (autoDismissOrOptions && typeof autoDismissOrOptions === 'object') {
    options = { ...autoDismissOrOptions };
  }
  options.type = type;
  options.loading = false;
  return options;
}

function createController(record) {
  const controller = {
    id: record.id,
    update(message, typeOrOptions) {
      return applyRecordUpdate(record, normalizeToastInput(message, typeOrOptions));
    },
    success(message, autoDismissOrOptions = true, maybeOptions) {
      const options = outcomeOptions('success', autoDismissOrOptions, maybeOptions);
      const input =
        message && typeof message === 'object' && !Array.isArray(message)
          ? normalizeToastInput({ ...message, ...options })
          : normalizeToastInput(message, options);
      return applyRecordUpdate(record, input);
    },
    error(message, options = {}) {
      const outcome = { ...(options && typeof options === 'object' ? options : {}) };
      outcome.type = 'error';
      outcome.loading = false;
      const input =
        message && typeof message === 'object' && !Array.isArray(message)
          ? normalizeToastInput({ ...message, ...outcome })
          : normalizeToastInput(message, outcome);
      return applyRecordUpdate(record, input);
    },
    dismiss() {
      dismissRecord(record);
    }
  };
  return controller;
}

function makeRecord(normalized, explicitId) {
  const options = normalized.options;
  const loading = options.loading === true;
  const type = normalizeType(options.type, loading ? 'info' : 'info'); // [builder-divergence 3] bare default success→info (D2)
  const action = readAction(options, undefined, true);
  const duration =
    typeof options.duration === 'number'
      ? Math.max(0, options.duration)
      : defaultToastDuration(type, !!action.label, loading);
  const record = {
    kind: 'toast',
    id: explicitId || `auto-toast-${++toastIdCounter}`,
    explicitId,
    title: normalized.title,
    detail: normalized.detail,
    message: normalized.message,
    type,
    loading,
    duration,
    actionLabel: action.label,
    onActionClick: action.handler,
    actionConsumed: false,
    dismissible: options.dismissible !== false,
    legacyProgress: options._legacyProgress === true,
    priority: 0,
    sequence: 0,
    mounted: false,
    removed: false,
    el: null,
    copy: null,
    icon: null,
    actionBtn: null,
    closeBtn: null,
    progressEl: null,
    hideTimer: null,
    queueExpiryTimer: null,
    timerStartedAt: 0,
    remainingMs: duration,
    pauseReasons: new Set(),
    handleVisibilityChange: null,
    controller: null,
    undoCommitTimer: null,
    undoTickTimer: null
  };
  record.priority = priorityFor(record);
  record.controller = createController(record);
  return record;
}

/**
 * @param {string|Object} message Legacy copy or a structured options object.
 * @param {string|Object} [typeOrOptions] Tone string or options. Supported
 * options include title, detail/description, type, loading, duration, id,
 * dismissible, and either `{ action: 'View', onClick }` or
 * `{ action: { label: 'View', onClick } }`.
 * @returns {{id: string, update: Function, success: Function, error: Function,
 *   dismiss: Function}}
 */
export function showToast(message, typeOrOptions) {
  resetDetachedToastState();
  const normalized = normalizeToastInput(message, typeOrOptions);
  const explicitId = normalized.options.id == null ? null : String(normalized.options.id);

  if (explicitId && toastsById.has(explicitId)) {
    const existing = toastsById.get(explicitId);
    if (!existing.removed) {
      applyRecordUpdate(existing, normalized, { replace: true });
      return existing.controller;
    }
    toastsById.delete(explicitId);
  }

  const record = makeRecord(normalized, explicitId);
  toastsById.set(record.id, record);
  if (activeToasts.length < MAX_VISIBLE_TOASTS) mountToast(record);
  else enqueueToast(record);
  return record.controller;
}

export function dismissToast(id) {
  resetDetachedToastState();
  const record = toastsById.get(String(id));
  if (!record || record.removed) return false;
  dismissRecord(record);
  return true;
}

// [builder-divergence 7] builder-compat exports: dismiss-by-id + prefix sweep.
// dismissToastById frees the active slot IMMEDIATELY (no 300ms exit) so the
// ErrorToastCoalescer's cap-3 eviction can promote a fresh error in the same
// synchronous burst — otherwise a lingering exit node holds a slot at cap 4 and
// the replacement error queues instead of mounting (renderer-race cap-3 contract).
export function dismissToastById(id) {
  resetDetachedToastState();
  const record = toastsById.get(String(id));
  if (!record || record.removed) return false;
  dismissRecord(record, { immediate: true });
  return true;
}

/**
 * Dismiss every live record whose id starts with `prefix`. Unlike the old
 * DOM-only sweep this also catches QUEUED (unmounted) records, and routes undo
 * records through commit (dismiss === commit).
 */
export function dismissToastsByPrefix(prefix) {
  const key = String(prefix == null ? '' : prefix);
  Array.from(toastsById).forEach(([, record]) => {
    if (!record.removed && String(record.id).startsWith(key)) dismissRecord(record);
  });
}

export function dismissAllToasts() {
  resetDetachedToastState();
  const callbackErrors = [];
  const wasResetting = resettingToasts;
  resettingToasts = true;
  try {
    [...queuedToasts, ...activeToasts].forEach((record) => {
      try {
        dismissRecord(record);
      } catch (error) {
        callbackErrors.push(error);
      }
    });
  } finally {
    resettingToasts = wasResetting;
  }
  rethrowCollectedErrors(callbackErrors, 'Dismiss all toasts');
}

export function _resetToastsForTests() {
  resettingToasts = true;
  activeToasts.forEach(abandonToastRecord);
  queuedToasts.forEach(abandonToastRecord);
  activeToasts.splice(0);
  queuedToasts.splice(0);
  toastsById.clear();
  toastIdCounter = 0;
  queueSequence = 0;
  document.querySelector('#pb-toast-container .pb-toast-overflow-indicator')?.remove();
  resettingToasts = false;
}

/**
 * Backward-compatible long-running toast. It is now an adapter over showToast,
 * so loading work receives the same cap, queue, actions, accessibility, and
 * cleanup behavior as every other notification.
 */
export function showProgressToast(message, options = {}) {
  const controller = showToast(message, {
    ...options,
    type: normalizeType(options.type, 'info'),
    loading: true,
    duration: 0,
    _legacyProgress: true
  });
  let settled = false;
  const adapter = {
    id: controller.id,
    update(nextMessage, nextOptions) {
      if (settled) return adapter;
      controller.update(nextMessage, {
        ...(nextOptions && typeof nextOptions === 'object' ? nextOptions : {}),
        loading: true,
        duration: 0
      });
      return adapter;
    },
    resolve(nextMessage, type, duration) {
      if (settled) return adapter;
      settled = true;
      controller.update(nextMessage, {
        type: normalizeType(type, 'success'),
        loading: false,
        duration: typeof duration === 'number' ? duration : 3000
      });
      return adapter;
    },
    success(nextMessage, autoDismissOrOptions = true, maybeOptions) {
      if (settled) return adapter;
      settled = true;
      controller.success(nextMessage, autoDismissOrOptions, maybeOptions);
      return adapter;
    },
    error(nextMessage, nextOptions) {
      if (settled) return adapter;
      settled = true;
      controller.error(nextMessage, nextOptions);
      return adapter;
    },
    dismiss() {
      if (settled) return;
      settled = true;
      controller.dismiss();
    }
  };
  return adapter;
}

/**
 * Deferred-action toast with a single countdown and an explicit Undo affordance.
 *
 * Unlike `showToast`, clicks on the toast body are deliberately inert. Calling
 * `dismiss()` means the pending action should be committed (the same policy as
 * Gmail's undo-send); `cancel()` follows the Undo path. Every settlement route
 * is guarded so a countdown racing a click can invoke at most one callback.
 *
 * @param {string} message
 * @param {{
 *   seconds?: number,
 *   onCommit?: () => void,
 *   onUndo?: () => void,
 *   type?: string,
 *   id?: string
 * }} [options] `id` (builder-divergence 6) commits+replaces any unsettled undo with the same id.
 * @returns {{dismiss: () => void, cancel: () => void, isSettled: () => boolean}}
 */
export function showTransactionalUndoToast(message, options = {}) {
  // [builder-divergence 5] renamed from showUndoToast (D3)
  resetDetachedToastState();
  const seconds = Math.max(
    0,
    Math.ceil(Number.isFinite(Number(options.seconds)) ? Number(options.seconds) : 5)
  );
  // [builder-divergence 6] optional explicit id with same-key-commits-before-replace
  // (hoisted from deferred-ops policy): a repeated drag replaces its chip; the prior
  // pending transaction is committed exactly once before the new record claims the id.
  // Immediate/no-promote teardown keeps the replacement chip visible even at cap.
  const explicitId = options.id == null ? null : String(options.id);
  if (explicitId) {
    const existing = toastsById.get(explicitId);
    if (existing && existing.kind === 'undo' && !existing.undoSettled) {
      settleUndoRecord(existing, 'commit', { immediate: true, promote: false });
    }
  }
  const normalized = normalizeToastInput(message, {
    type: normalizeType(options.type, 'info'),
    duration: 0,
    dismissible: false
  });
  const record = makeRecord(normalized, explicitId); // [builder-divergence 6]
  record.kind = 'undo';
  record.type = normalizeType(options.type, 'info');
  record.duration = 0;
  record.remainingMs = 0;
  record.actionLabel = 'Undo';
  record.onActionClick = null;
  record.dismissible = false;
  record.onCommit = typeof options.onCommit === 'function' ? options.onCommit : null;
  record.onUndo = typeof options.onUndo === 'function' ? options.onUndo : null;
  record.undoRemainingMs = seconds * 1000;
  record.undoTimerStartedAt = 0;
  record.undoCommitTimer = null;
  record.undoTickTimer = null;
  record.undoCountdownEl = null;
  record.undoMessageEl = null;
  record.undoSettled = false;
  record.priority = priorityFor(record);
  toastsById.set(record.id, record);

  if (activeToasts.length < MAX_VISIBLE_TOASTS) mountToast(record);
  else enqueueToast(record);

  const handle = {
    id: record.id,
    dismiss() {
      settleUndoRecord(record, 'commit');
    },
    cancel() {
      settleUndoRecord(record, 'undo');
    },
    isSettled() {
      return record.undoSettled;
    }
  };
  return handle;
}
